import type {
  ChatClaimDisclaimerItem,
  ChatTransitionItem,
  ChatWidgetItem,
} from "@health/ui/components/chat/types";
import { extractSaveIntakeInlineWidgets } from "../internal/intake/SaveIntakeFields.claim";
import { extractSaveIntakeTransition } from "../internal/intake/SaveIntakeFields.transition";
import { extractClaimDisclaimerFooter } from "../internal/widgets/ClaimWidget.footer";

// ---------------------------------------------------------------------------
// CHAT_TOOL_CONFIG — single per-tool registry of render-layer behavior.
//
// This is the renderer-side parallel to `TOOL_METADATA` in `@health/types`.
// `TOOL_METADATA` is platform-neutral (kind/domain/availability/autoSubmit)
// and ships to every consumer of tool metadata, including server and future
// mobile renderers. UI-specific behaviors (footer/gate projection, status
// visibility, slow escalation) cannot live there without dragging
// `@health/ui` chat-item types into the platform-neutral package. They live
// here instead, scoped to the chat surface.
//
// Everything per-tool that affects how `normalize.ts` / `ChatPanel.tsx` /
// `ToolActivityStatus.tsx` render the chat feed lands in this table.
// Adding a new render-layer concern → add a field to `ChatToolConfig` and
// an accessor; never reach into `CHAT_TOOL_CONFIG[toolName]` directly from
// consumers.
//
// Boundary rule: this file MUST NOT import from
// `internal/chat/tool-status-copy.ts`. Reverse direction (tool-status-copy
// importing accessors from here) is fine. Keeping the dependency one-way
// preserves this module as a leaf and prevents cycles.
//
// Co-location convention for projection extractors:
//   - footer extractors → `internal/widgets/<Widget>.footer.ts`
//   - gate extractors   → `internal/intake/<Tool>.transition.ts`
// The implementations live next to their widget/tool; this file only wires
// them into the registry.
//
// Relationship to `isUserMessagePart` (in `chat/normalize.ts`):
// Layout/grouping boundaries (does this tool end a "user step"?) are NOT
// configured here. They're driven by `TOOL_METADATA` in `@health/types`
// — specifically, `autoSubmit: true` widgets are never user-step
// boundaries (handled by `isUserMessagePart`'s early return). If you need
// a tool to be auto-submit at the agent loop but still flush as a user
// boundary in the chat feed, that's a missing axis — split metadata or
// add a new field here, don't repurpose `autoSubmit`.
// ---------------------------------------------------------------------------

/**
 * Minimal shape of a tool part that extractors consume. Kept independent
 * of the AI SDK `UIMessage` part type so extractors don't accidentally
 * reach into unrelated fields — and so the normalizer's internal
 * `ToolPart` remains structurally assignable.
 */
export type ExtractorToolPart = {
  state: string;
  input: Record<string, unknown>;
  output?: unknown;
};

/**
 * Footer extractor — projects a tool part into a footer item docked to
 * the bottom of the current chat step. Pure UI placement; never affects
 * progression. Returns `null` when the tool part doesn't yet warrant a
 * footer (still streaming, empty payload, no footer for this instance).
 */
export type FooterExtractor = (
  messageId: string,
  partIdx: number,
  toolPart: ExtractorToolPart,
) => ChatClaimDisclaimerItem | null;

/**
 * Gate extractor — projects a tool part into an item that closes the
 * current step and gates the next step until the user acts on it. Pure
 * progression directive; the item's `kind` decides how it renders.
 * Returns `null` when the tool part doesn't yet warrant gating.
 */
export type GateExtractor = (
  messageId: string,
  partIdx: number,
  toolPart: ExtractorToolPart,
) => ChatTransitionItem | null;

/**
 * Inline-widgets extractor — projects a tool part into widget items the
 * SDK renders inline, alongside any disclaimer footer items those widgets
 * carry. Used for the F3 Option B path where `save_intake_fields` returns
 * a pre-resolved claim widget payload, so the model does not need to emit
 * a chained `show_claim` step. Always returns arrays (possibly empty) so
 * the call site is branch-free.
 */
export type InlineWidgetsExtractor = (
  messageId: string,
  partIdx: number,
  toolPart: ExtractorToolPart,
) => { widgetItems: ChatWidgetItem[]; footerItems: ChatClaimDisclaimerItem[] };

/** Per-tool render-layer configuration. All fields optional. */
export type ChatToolConfig = {
  /** Projects tool part → footer item docked to the bottom of the
   *  current step. Pure UI placement; never affects progression. */
  footerExtractor?: FooterExtractor;

  /** Projects tool part → gate item that closes the current step and
   *  gates the next step until dismissed (stage transition pattern). */
  gateExtractor?: GateExtractor;

  /** Projects tool part → inline widget items + disclaimer footers. Used
   *  when a server tool's output carries pre-resolved widget payloads the
   *  SDK renders directly, sparing the model an extra chained step (F3
   *  Option B). */
  inlineWidgetsExtractor?: InlineWidgetsExtractor;

  /** Show a user-facing status indicator (`ToolActivityStatus`) while this
   *  tool runs. Internal bookkeeping tools (no meaningful user-facing
   *  status) leave this off. */
  visibleStatus?: boolean;

  /** Tool warrants progressive status escalation per `ESCALATION`
   *  thresholds in `tool-status-copy.ts`. Implies `visibleStatus: true`
   *  in practice — escalation only makes sense for tools that show status. */
  slowEscalation?: boolean;

  /** Widget should be placed in the current group without triggering a
   *  group boundary, even when the previous item was a user-boundary. Use
   *  for auxiliary widgets (e.g. show_reasoning_trace) that should float
   *  alongside adjacent content rather than occupying their own step. */
  noBoundary?: boolean;
};

/**
 * Single source of truth for per-tool render-layer behavior on the chat
 * surface. Sparse by design — only tools with non-default behavior need
 * an entry. Lookups for missing tools return `undefined` (or `false` via
 * the boolean accessors), which is the correct default in every case.
 *
 * Adding a tool with new render behavior:
 *   1. If a new field is needed, add it to `ChatToolConfig` + an accessor.
 *   2. Add an entry here keyed by tool name.
 *   3. For projection extractors: create the `<Widget>.footer.ts` /
 *      `<Tool>.transition.ts` file alongside the widget/tool, export a
 *      typed extractor, and import it here.
 */
export const CHAT_TOOL_CONFIG: Record<string, ChatToolConfig> = {
  show_claim: {
    footerExtractor: extractClaimDisclaimerFooter,
  },
  show_reasoning_trace: {
    noBoundary: true,
  },
  save_intake_fields: {
    gateExtractor: extractSaveIntakeTransition,
    inlineWidgetsExtractor: extractSaveIntakeInlineWidgets,
    visibleStatus: true,
  },
  check_eligibility: { visibleStatus: true, slowEscalation: true },
  search_catalog: { visibleStatus: true },
  get_filtered_offers: { visibleStatus: true, slowEscalation: true },
  visit: { visibleStatus: true, slowEscalation: true },
  get_patient: { visibleStatus: true },
  save_memory: { visibleStatus: true },
  search_memory: { visibleStatus: true },
};

/** Full per-tool config (useful for debug surfaces that want to inspect everything). */
export const getChatToolConfig = (tool: string): ChatToolConfig | undefined =>
  CHAT_TOOL_CONFIG[tool];

export const getFooterExtractor = (tool: string): FooterExtractor | undefined =>
  CHAT_TOOL_CONFIG[tool]?.footerExtractor;

export const getGateExtractor = (tool: string): GateExtractor | undefined =>
  CHAT_TOOL_CONFIG[tool]?.gateExtractor;

export const getInlineWidgetsExtractor = (tool: string): InlineWidgetsExtractor | undefined =>
  CHAT_TOOL_CONFIG[tool]?.inlineWidgetsExtractor;

export const hasVisibleStatus = (tool: string): boolean =>
  CHAT_TOOL_CONFIG[tool]?.visibleStatus === true;

export const hasSlowEscalation = (tool: string): boolean =>
  CHAT_TOOL_CONFIG[tool]?.slowEscalation === true;

export const hasNoBoundary = (tool: string): boolean => CHAT_TOOL_CONFIG[tool]?.noBoundary === true;
